'use strict'
import React, { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress
} from '@material-ui/core'
import { DEFAULT_CURVE } from './crewEfficiency'

const CREW_SIZES = [1, 2, 3, 4, 5, 6]
const MIN_FACTOR = 0.5
const MAX_FACTOR = 2.0

const isValidFactor = (value) => {
  const num = Number(value)
  return value !== '' && Number.isFinite(num) && num >= MIN_FACTOR && num <= MAX_FACTOR
}

const curveToValues = (factors) => {
  const next = {}
  CREW_SIZES.forEach(size => {
    const factor = Number(factors?.[size])
    next[size] = String(Number.isFinite(factor) ? factor : 1)
  })
  return next
}

export default function CrewEfficiencyEditor ({ open, onClose, url, onSaved }) {
  const [values, setValues] = useState(() => curveToValues(DEFAULT_CURVE))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setError(null)
    axios.get(`${url}/api/hmi/territory/config/crew-efficiency`)
      .then(response => {
        if (cancelled) return
        setValues(curveToValues(response.data?.factors || DEFAULT_CURVE))
      })
      .catch(() => {
        if (cancelled) return
        setValues(curveToValues(DEFAULT_CURVE))
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, url])

  const allValid = CREW_SIZES.every(size => isValidFactor(values[size]))

  const handleChange = (size) => (event) => {
    setValues(prev => ({ ...prev, [size]: event.target.value }))
  }

  const handleSave = async () => {
    if (!allValid) return
    setSaving(true)
    setError(null)
    try {
      const factors = {}
      CREW_SIZES.forEach(size => { factors[size] = Number(values[size]) })
      await axios.put(`${url}/api/hmi/territory/config/crew-efficiency`, { factors })
      if (onSaved) onSaved('Crew efficiency curve saved')
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save crew efficiency curve')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Crew Efficiency Curve</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Efficiency multiplier per crew size. Example: 1-man = 1.15 means a solo visit
          needs 15% more total man-hours.
        </Typography>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {CREW_SIZES.map(size => (
              <TextField
                key={size}
                label={`${size}-man crew`}
                value={values[size] ?? ''}
                onChange={handleChange(size)}
                type="number"
                size="small"
                variant="outlined"
                error={!isValidFactor(values[size])}
                helperText="× man-hours required (1.0 = linear)"
                InputProps={{ inputProps: { min: MIN_FACTOR, max: MAX_FACTOR, step: 0.05 } }}
              />
            ))}
          </div>
        )}
        {!allValid && !loading && (
          <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
            Factors must be between {MIN_FACTOR} and {MAX_FACTOR}.
          </Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || saving || !allValid}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
